import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAiConfirm, AI_CONFIRM_NATF } from "../components/AiConfirmDialog";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Handshake, Plus, Sparkle, UploadSimple, Trash, FilePdf, FileDoc, ArrowLeft, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";

const CAT_BADGE = {
  "Low Risk": "border-emerald-300 bg-emerald-50 text-emerald-700",
  "Medium Risk": "border-amber-300 bg-amber-50 text-amber-700",
  "High Risk": "border-red-300 bg-red-50 text-red-700",
};

// Client-side mirror of the backend scoring so the panel updates live.
const riskValue = (polarity, answer) => {
  const good = polarity === "negative" ? "No" : "Yes";
  if (answer === good || answer === "N/A") return 0;
  if (answer === "Unknown" || (answer && answer !== good)) return 3;
  return 0;
};
const multiplierFor = (s) => {
  const n = parseFloat(s);
  if (isNaN(n)) return 1.0;
  if (n >= 90) return 0.9; if (n >= 80) return 1.0; if (n >= 70) return 1.1; if (n >= 60) return 1.2; return 1.3;
};
const categoryFor = (t) => (t <= 8 ? "Low Risk" : t <= 24 ? "Medium Risk" : "High Risk");
const rank = (c) => ({ "Low Risk": 1, "Medium Risk": 2, "High Risk": 3 }[c] || 1);
const mitigationFor = (p, v) => {
  const r = Math.max(rank(p), rank(v));
  if (r >= 3) return "Deploy 2 or more Additional Security Controls as needed to mitigate risks.";
  if (r === 2) return "Deploy 1 or more Additional Security Controls as needed to mitigate risks.";
  return "Deployment of existing or more Security Controls.";
};

export default function Cip013Scrm() {
  const { isSubHidden } = useModuleVisibility();
  const [config, setConfig] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [active, setActive] = useState(null); // full assessment object being edited
  const [showNew, setShowNew] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", url: "", headquarters: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const loadVendors = useCallback(() => {
    api.get("/scrm/vendors").then((r) => setVendors(r.data)).catch(() => setVendors([]));
  }, []);
  useEffect(() => {
    api.get("/scrm/config").then((r) => setConfig(r.data)).catch(() => {});
    loadVendors();
  }, [loadVendors]);

  const openAssessment = async (aid) => {
    try {
      const { data } = await api.get(`/scrm/assessments/${aid}`);
      setActive(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createVendorAndAssess = async () => {
    if (!newVendor.name.trim()) { toast.error("Vendor name is required."); return; }
    setBusy(true);
    try {
      const { data: v } = await api.post("/scrm/vendors", newVendor);
      const { data: a } = await api.post(`/scrm/vendors/${v.id}/assessments`);
      setShowNew(false);
      setNewVendor({ name: "", url: "", headquarters: "", contact: "" });
      setActive(a);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const newAssessmentFor = async (vid) => {
    try {
      const { data } = await api.post(`/scrm/vendors/${vid}/assessments`);
      setActive(data);
      loadVendors();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delVendor = async (vid) => {
    if (!window.confirm("Delete this vendor and all its assessments?")) return;
    try { await api.delete(`/scrm/vendors/${vid}`); loadVendors(); toast.success("Vendor deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (active) return (
    <AssessmentEditor
      config={config} active={active} setActive={setActive}
      onBack={() => { setActive(null); loadVendors(); }}
      aiRunning={aiRunning} setAiRunning={setAiRunning}
      saving={saving} setSaving={setSaving} fileRef={fileRef}
    />
  );

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="scrm-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Handshake size={26} weight="duotone" /> CIP-013 — Supply Chain Risk Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Vendor registry & CIP-013-2 risk assessments. AI research and NATF form parsing supported.</p>
        </div>
        {!isSubHidden("scrm:new_vendor") && <Button data-testid="scrm-new-vendor-btn" onClick={() => setShowNew(true)} className="gap-2">
          <Plus size={16} /> New Vendor
        </Button>}
      </div>

      <DataResidencyNote className="mb-6" />

      {vendors === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : vendors.length === 0 ? (
        <div className="border border-dashed rounded-lg p-10 text-center text-muted-foreground" data-testid="scrm-empty">
          No vendors yet. Add your first vendor to begin a CIP-013 assessment.
        </div>
      ) : (
        <div className="space-y-2" data-testid="scrm-vendor-list">
          {vendors.map((v) => {
            const la = v.latest_assessment;
            return (
              <div key={v.id} data-testid={`scrm-vendor-${v.id}`}
                className="border rounded-lg p-4 flex items-center justify-between hover:border-border bg-card">
                <div className="min-w-0">
                  <div className="font-semibold text-foreground flex items-center gap-2">
                    {v.name}
                    {la?.lsipa_flag && <span className="text-[10px] uppercase font-bold border border-red-400 bg-red-50 text-red-700 px-1.5 py-0.5 rounded">LSIPA</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{v.url || "—"} · {v.headquarters || "—"}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {la ? (
                    <div className="text-right">
                      <span className={`text-[11px] font-bold border px-2 py-0.5 rounded ${CAT_BADGE[la.vendor_risk_category] || "border-border bg-muted text-muted-foreground"}`}>
                        {la.vendor_risk_category || "Not scored"} {la.total_risk_score != null && `(${la.total_risk_score})`}
                      </span>
                      <div className="text-[10px] text-muted-foreground mt-1">valid to {la.valid_until || "—"} · {la.status}</div>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">No assessment</span>}
                  <Button size="sm" variant="outline" data-testid={`scrm-open-${v.id}`}
                    onClick={() => (la ? openAssessment(la.id) : newAssessmentFor(v.id))}>
                    {la ? "Open" : "Assess"}
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`scrm-newassess-${v.id}`} onClick={() => newAssessmentFor(v.id)}>New</Button>
                  <button data-testid={`scrm-delvendor-${v.id}`} onClick={() => delVendor(v.id)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="scrm-new-vendor-dialog">
          <DialogHeader>
            <DialogTitle>New Vendor</DialogTitle>
            <DialogDescription>Add a vendor, then complete the CIP-013 assessment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Vendor name *</Label><Input data-testid="scrm-vendor-name" value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })} /></div>
            <div><Label>Website URL</Label><Input data-testid="scrm-vendor-url" placeholder="example.com" value={newVendor.url} onChange={(e) => setNewVendor({ ...newVendor, url: e.target.value })} /></div>
            <div><Label>Headquarters</Label><Input data-testid="scrm-vendor-hq" value={newVendor.headquarters} onChange={(e) => setNewVendor({ ...newVendor, headquarters: e.target.value })} /></div>
            <div><Label>Contact</Label><Input data-testid="scrm-vendor-contact" value={newVendor.contact} onChange={(e) => setNewVendor({ ...newVendor, contact: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button data-testid="scrm-create-vendor-btn" disabled={busy} onClick={createVendorAndAssess}>{busy ? "Creating…" : "Create & Assess"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssessmentEditor({ config, active, setActive, onBack, aiRunning, setAiRunning, saving, setSaving, fileRef }) {
  const { confirmAi, aiConfirmDialog } = useAiConfirm();
  const { isSubHidden } = useModuleVisibility();
  const [form, setForm] = useState(() => normalize(active, config));
  useEffect(() => { setForm(normalize(active, config)); }, [active, config]);

  const answers = form.answers;
  const setAnswer = (qid, patch) => setForm((f) => ({
    ...f, answers: f.answers.map((a) => (a.qid === qid ? { ...a, ...patch } : a)),
  }));

  const subtotal = useMemo(() => answers.reduce((s, a) => s + riskValue(a.polarity, a.answer), 0), [answers]);
  const requiredQs = useMemo(() => answers.filter((a) => a.subpart), [answers]);
  const requiredAnswered = useMemo(() => requiredQs.filter((a) => a.answer), [requiredQs]);
  const requiredUnanswered = requiredQs.length - requiredAnswered.length;
  const mult = form.multiplier_override ? parseFloat(form.multiplier_override) : multiplierFor(form.security_scorecard_score);
  const total = Math.round(subtotal * mult * 10) / 10;
  const vendorCat = categoryFor(total);
  const mitigation = mitigationFor(form.project_risk, vendorCat);
  const lsipa = form.prechecks.lsipa === "Yes";

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = {
        prechecks: form.prechecks, precheck_notes: form.precheck_notes,
        security_scorecard_score: form.security_scorecard_score === "" ? null : parseFloat(form.security_scorecard_score),
        multiplier_override: form.multiplier_override === "" ? null : parseFloat(form.multiplier_override),
        project_risk: form.project_risk, project_risk_basis: form.project_risk_basis,
        answers: answers.map((a) => ({ qid: a.qid, answer: a.answer, supporting_info: a.supporting_info, ai_draft: a.ai_draft, ai_rationale: a.ai_rationale })),
        controls: form.controls, custom_control_text: form.custom_control_text,
        threat_assessment_notes: form.threat_assessment_notes, review_evidence_folder: form.review_evidence_folder,
        status: status || form.status, assessment_date: form.assessment_date,
      };
      const { data } = await api.put(`/scrm/assessments/${active.id}`, payload);
      setForm((f) => ({ ...f, status: data.status }));
      toast.success(status === "complete" ? "Assessment marked complete" : "Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const completeAssessment = () => {
    if (requiredUnanswered > 0) {
      const ok = window.confirm(
        `${requiredUnanswered} CIP-013-2 subpart question(s) are still unanswered.\n\n` +
        `These map to mandatory NERC CIP-013-2 R1 requirement subparts and must be completed or at least explicitly considered (mark Unknown/N/A with a note).\n\nMark complete anyway?`
      );
      if (!ok) return;
    }
    save("complete");
  };

  const runResearch = async () => {
    setAiRunning(true);
    try {
      const res = await runAiJob(`/scrm/assessments/${active.id}/ai-research`, {});
      applyAi(res);
      toast.success("AI research complete — review the drafted answers.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiRunning(false); }
  };

  const onNatf = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!(await confirmAi(AI_CONFIRM_NATF))) return;
    setAiRunning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await runAiJob(`/scrm/assessments/${active.id}/parse-natf?confirm_confidential=1`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyAi(res);
      toast.success("NATF form parsed — review the drafted answers.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiRunning(false); }
  };

  const applyAi = (res) => {
    const byId = {}; (res.answers || []).forEach((a) => { byId[a.qid] = a; });
    setForm((f) => ({
      ...f,
      answers: f.answers.map((a) => (byId[a.qid] ? { ...a, answer: byId[a.qid].answer, supporting_info: byId[a.qid].supporting_info || a.supporting_info, ai_draft: true, ai_rationale: byId[a.qid].ai_rationale || "", source_url: byId[a.qid].source_url || "" } : a)),
      controls: (res.suggested_controls && res.suggested_controls.length && !f.controls.length) ? res.suggested_controls.map(String) : f.controls,
      security_scorecard_score: res.scorecard_estimate != null && res.scorecard_estimate !== "" && (f.security_scorecard_score === "" || f.security_scorecard_score == null) ? String(res.scorecard_estimate) : f.security_scorecard_score,
      ai_summary: res.summary || f.ai_summary, ai_sources: res.sources || f.ai_sources,
    }));
  };

  const toggleControl = (cid) => setForm((f) => ({
    ...f, controls: f.controls.includes(cid) ? f.controls.filter((c) => c !== cid) : [...f.controls, cid],
  }));

  const exportFile = async (fmt) => {
    try {
      const resp = await api.get(`/scrm/assessments/${active.id}/export?format=${fmt}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: fmt === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf" });
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      const safe = (form.vendor_name || "vendor").replace(/[^A-Za-z0-9]+/g, "_");
      link.download = `${safe}_CIP013_assessment.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
  };

  if (!config) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="scrm-editor">
      {aiConfirmDialog}
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} data-testid="scrm-back-btn" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={16} /> Back to vendors</button>
        <div className="flex items-center gap-2">
          <button onClick={() => exportFile("pdf")} data-testid="scrm-export-pdf" className="inline-flex items-center gap-1 text-sm border rounded px-3 py-1.5 hover:bg-muted"><FilePdf size={16} /> PDF</button>
          <button onClick={() => exportFile("docx")} data-testid="scrm-export-docx" className="inline-flex items-center gap-1 text-sm border rounded px-3 py-1.5 hover:bg-muted"><FileDoc size={16} /> Word</button>
          <Button variant="outline" data-testid="scrm-save-btn" disabled={saving} onClick={() => save()}>{saving ? "Saving…" : "Save"}</Button>
          <Button data-testid="scrm-complete-btn" disabled={saving} onClick={completeAssessment}>Mark Complete</Button>
        </div>
      </div>

      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{form.vendor_name}</h1>
          <p className="text-sm text-muted-foreground">{form.headquarters || "—"} · Assessment {form.assessment_date} · valid to {computedValidUntil(form.assessment_date)} · <span className="uppercase font-semibold">{form.status}</span></p>
        </div>
      </div>

      {/* AI toolbar */}
      <div className="border rounded-lg p-4 mb-5 bg-muted flex flex-wrap items-center gap-3" data-testid="scrm-ai-toolbar">
        <span className="text-sm font-semibold text-foreground">Auto-complete:</span>
        {!isSubHidden("scrm:research") && <Button size="sm" variant="outline" className="gap-2" data-testid="scrm-ai-research-btn" disabled={aiRunning} onClick={runResearch}>
          <Sparkle size={15} /> {aiRunning ? "Working…" : "AI Research (from vendor website)"}
        </Button>}
        {!isSubHidden("scrm:natf") && <Button size="sm" variant="outline" className="gap-2" data-testid="scrm-natf-btn" disabled={aiRunning} onClick={() => fileRef.current?.click()}>
          <UploadSimple size={15} /> Upload completed NATF form
        </Button>}
        <input ref={fileRef} type="file" accept=".pdf,.xlsx,.docx,.csv,.txt" className="hidden" onChange={onNatf} data-testid="scrm-natf-input" />
        <span className="text-xs text-muted-foreground">AI answers are drafts — verify each before completing.</span>
      </div>

      {form.ai_summary && (
        <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 mb-5 text-sm text-sky-900" data-testid="scrm-ai-summary">
          <span className="font-semibold">AI summary:</span> {form.ai_summary}
          {form.ai_sources && form.ai_sources.length > 0 && (
            <div className="mt-2 pt-2 border-t border-sky-200" data-testid="scrm-ai-sources">
              <span className="text-xs font-semibold text-sky-800">Sources crawled ({form.ai_sources.length}):</span>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                {form.ai_sources.map((s) => (
                  <a key={s} href={s} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 underline truncate max-w-[280px]">{s}</a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {lsipa && (
        <div className="border border-red-300 bg-red-50 rounded-lg p-3 mb-5 text-sm text-red-800 flex items-center gap-2 font-semibold" data-testid="scrm-lsipa-alert">
          <WarningCircle size={18} weight="fill" /> LSIPA / restricted-country flag set — PULL ASSESSMENT and escalate to Compliance.
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-5">
          {/* Prechecks */}
          <section className="border rounded-lg p-4" data-testid="scrm-prechecks">
            <h2 className="font-semibold text-foreground mb-3">Screening & Prior Assessment</h2>
            <div className="space-y-3">
              {config.prechecks.map((p) => (
                <div key={p.id} className="text-sm" data-testid={`scrm-precheck-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-muted-foreground font-mono text-xs pt-2 w-8 shrink-0">{p.num}</span>
                    <div className="flex-1">
                      <p className="text-foreground">{p.question}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <select data-testid={`scrm-precheck-select-${p.id}`} className="border rounded px-2 py-1 text-sm"
                          value={form.prechecks[p.id] || ""} onChange={(e) => setForm((f) => ({ ...f, prechecks: { ...f.prechecks, [p.id]: e.target.value } }))}>
                          <option value="">—</option>
                          {config.answers.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                        {form.prechecks[p.id] && (
                          <span className={`text-xs ${form.prechecks[p.id] === p.pull_on ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                            {form.prechecks[p.id] === "Yes" ? p.action_yes : form.prechecks[p.id] === "No" ? p.action_no : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Questionnaire */}
          <section className="border rounded-lg p-4" data-testid="scrm-questions">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-foreground">CIP-013-2 Risk Questionnaire</h2>
              <span data-testid="scrm-required-counter"
                className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${requiredUnanswered === 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                CIP-013-2 required: {requiredAnswered.length}/{requiredQs.length} answered
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Questions tagged with a CIP-013-2 R1 subpart are <span className="font-semibold">required</span> — complete them, or at minimum consider them (mark Unknown/N/A with a note).</p>
            <div className="space-y-4">
              {answers.map((a) => {
                const rv = riskValue(a.polarity, a.answer);
                return (
                  <div key={a.qid} className="border-b pb-3 last:border-0" data-testid={`scrm-q-${a.qid}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-muted-foreground font-mono text-xs pt-2 w-8 shrink-0">{a.num}</span>
                      <div className="flex-1">
                        <p className="text-sm text-foreground">{a.question}</p>
                        {a.subpart && (
                          <p className="text-[11px] font-medium mt-0.5">
                            <span className="text-sky-600">{a.subpart}</span>
                            <span className={`ml-2 uppercase font-bold px-1.5 py-0.5 rounded border ${a.answer ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-600"}`}>Required</span>
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <select data-testid={`scrm-answer-${a.qid}`} className="border rounded px-2 py-1 text-sm"
                            value={a.answer || ""} onChange={(e) => setAnswer(a.qid, { answer: e.target.value, ai_draft: false })}>
                            <option value="">—</option>
                            {config.answers.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                          <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded border ${rv > 0 ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>risk {rv}</span>
                          {a.ai_draft && <span className="text-[10px] uppercase font-bold border border-violet-300 bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded" title={a.ai_rationale}>AI draft</span>}
                        </div>
                        {a.answer && <p className="text-[11px] text-muted-foreground mt-1.5"><span className="font-semibold">Action:</span> {a.action}</p>}
                        {a.ai_draft && a.ai_rationale && <p className="text-[11px] text-violet-600 mt-1 italic">AI: {a.ai_rationale}</p>}
                        {a.ai_draft && a.source_url && <a href={a.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-600 underline mt-0.5 inline-block truncate max-w-full" data-testid={`scrm-source-${a.qid}`}>Source: {a.source_url}</a>}
                        <Textarea data-testid={`scrm-support-${a.qid}`} className="mt-1.5 text-sm" rows={1} placeholder="Supporting information / evidence…"
                          value={a.supporting_info || ""} onChange={(e) => setAnswer(a.qid, { supporting_info: e.target.value })} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Controls */}
          <section className="border rounded-lg p-4" data-testid="scrm-controls">
            <h2 className="font-semibold text-foreground mb-1">Additional Security Controls</h2>
            <p className="text-xs text-muted-foreground mb-3">{mitigation}</p>
            <div className="space-y-2">
              {config.controls.map((c) => (
                <label key={c.id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`scrm-control-${c.id}`}>
                  <input type="checkbox" className="mt-1" checked={form.controls.includes(c.id)} onChange={() => toggleControl(c.id)} data-testid={`scrm-control-check-${c.id}`} />
                  <span className="text-foreground"><span className="font-semibold">Control {c.id}:</span> {c.text}</span>
                </label>
              ))}
              {form.controls.includes("12") && (
                <Textarea data-testid="scrm-custom-control" className="mt-2 text-sm" rows={2} placeholder="Describe the custom control…"
                  value={form.custom_control_text} onChange={(e) => setForm((f) => ({ ...f, custom_control_text: e.target.value }))} />
              )}
            </div>
          </section>

          <section className="border rounded-lg p-4" data-testid="scrm-threat">
            <h2 className="font-semibold text-foreground mb-2">Threat Assessment Notes</h2>
            <Textarea data-testid="scrm-threat-notes" rows={3} placeholder="Mitigation measures, coordination notes, exceptions…"
              value={form.threat_assessment_notes} onChange={(e) => setForm((f) => ({ ...f, threat_assessment_notes: e.target.value }))} />
          </section>
        </div>

        {/* Scoring panel */}
        <div className="space-y-4">
          <div className="border rounded-lg p-4 sticky top-4" data-testid="scrm-score-panel">
            <h2 className="font-semibold text-foreground mb-3">Risk Scoring</h2>
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">SecurityScorecard Score (0–100)</Label>
                <Input data-testid="scrm-scorecard-input" type="number" value={form.security_scorecard_score ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, security_scorecard_score: e.target.value }))} />
                <p className="text-[11px] text-muted-foreground mt-1">Grade {form.security_scorecard_score !== "" && form.security_scorecard_score != null ? gradeFor(form.security_scorecard_score) : "—"} → multiplier {mult}</p>
              </div>
              <div>
                <Label className="text-xs">Project Risk</Label>
                <select data-testid="scrm-project-risk" className="border rounded px-2 py-1.5 text-sm w-full"
                  value={form.project_risk} onChange={(e) => setForm((f) => ({ ...f, project_risk: e.target.value }))}>
                  {config.risk_levels.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Score subtotal</span><span className="font-semibold" data-testid="scrm-subtotal">{subtotal}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">× multiplier</span><span className="font-semibold">{mult}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Risk Score</span><span className="font-bold text-lg" data-testid="scrm-total">{total}</span></div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-muted-foreground">Vendor Risk</span>
                  <span className={`text-xs font-bold border px-2 py-0.5 rounded ${CAT_BADGE[vendorCat]}`} data-testid="scrm-category">{vendorCat}</span>
                </div>
              </div>
              <div className="border-t pt-3 text-xs text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Mitigation ({form.project_risk} × {vendorCat})</p>
                <p data-testid="scrm-mitigation">{mitigation}</p>
                <p className="text-[10px] text-muted-foreground mt-2">Thresholds: Low 0–8 · Medium 9–24 · High &gt;24</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalize(a, config) {
  const qcat = (config?.questions || []);
  const byId = {}; (a.answers || []).forEach((x) => { byId[x.qid] = x; });
  const answers = (qcat.length ? qcat : (a.answers || [])).map((q) => {
    const src = byId[q.id || q.qid] || {};
    return {
      qid: q.id || q.qid, num: q.num, subpart: q.subpart, question: q.question,
      action: q.action, polarity: q.polarity,
      answer: src.answer || "", supporting_info: src.supporting_info || "",
      ai_draft: !!src.ai_draft, ai_rationale: src.ai_rationale || "", source_url: src.source_url || "",
    };
  });
  return {
    vendor_name: a.vendor_name, headquarters: a.headquarters, assessment_date: a.assessment_date,
    status: a.status || "draft", prechecks: a.prechecks || {}, precheck_notes: a.precheck_notes || {},
    security_scorecard_score: a.security_scorecard_score ?? "", multiplier_override: a.multiplier_override ?? "",
    project_risk: a.project_risk || "Low Risk", project_risk_basis: a.project_risk_basis || "",
    controls: (a.controls || []).map(String), custom_control_text: a.custom_control_text || "",
    threat_assessment_notes: a.threat_assessment_notes || "", review_evidence_folder: a.review_evidence_folder || "",
    ai_summary: a.ai_summary || "", ai_sources: a.ai_sources || [], answers,
  };
}

const gradeFor = (s) => { const n = parseFloat(s); if (isNaN(n)) return "—"; return n >= 90 ? "A" : n >= 80 ? "B" : n >= 70 ? "C" : n >= 60 ? "D" : "F"; };
function computedValidUntil(dateStr) {
  try { const d = new Date(dateStr); d.setMonth(d.getMonth() + 15); return d.toISOString().slice(0, 10); } catch { return "—"; }
}
